import { useMemo, useState } from "react";
import { cmToFtIn, kgToLb } from "@/lib/health/body-profile";
import { useBodyProfileSync } from "@/lib/health/use-body-profile";
import { ftInToCm, lbToKg } from "../lib/health";
import type { Units } from "./inputs";

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Shared height/weight state with metric/imperial unit handling. Deterministic
 * defaults (175 cm / 70 kg) so a result renders server-side with no CLS.
 * Signed-in users get the values synced with their shared body profile —
 * both unit representations are written on prefill because the metric and
 * imperial fields are independent state. */
export function useBody() {
  const [units, setUnits] = useState<Units>("metric");
  const [cm, setCm] = useState("175");
  const [ft, setFt] = useState("5");
  const [inch, setInch] = useState("9");
  const [kg, setKg] = useState("70");
  const [lb, setLb] = useState("154");

  const heightCm = useMemo(
    () =>
      units === "metric"
        ? Number(cm)
        : ftInToCm(Number(ft || 0), Number(inch || 0)),
    [units, cm, ft, inch],
  );
  const weightKg = useMemo(
    () => (units === "metric" ? Number(kg) : lbToKg(Number(lb))),
    [units, kg, lb],
  );

  const profileSync = useBodyProfileSync({
    apply: (p) => {
      if (p.units) setUnits(p.units);
      if (p.heightCm) {
        setCm(String(round1(p.heightCm)));
        const imperial = cmToFtIn(p.heightCm);
        setFt(String(imperial.ft));
        setInch(String(imperial.inch));
      }
      if (p.weightKg) {
        setKg(String(round1(p.weightKg)));
        setLb(String(kgToLb(p.weightKg)));
      }
    },
    collect: () => ({
      units,
      heightCm:
        Number.isFinite(heightCm) && heightCm > 0
          ? round1(heightCm)
          : undefined,
      weightKg:
        Number.isFinite(weightKg) && weightKg > 0
          ? round1(weightKg)
          : undefined,
    }),
    deps: [units, cm, ft, inch, kg, lb],
  });

  return {
    units,
    setUnits,
    cm,
    setCm,
    ft,
    setFt,
    inch,
    setInch,
    kg,
    setKg,
    lb,
    setLb,
    heightCm,
    weightKg,
    profileSync,
  };
}
